import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, Sparkles, Paperclip } from "lucide-react";
import { HalEye } from "@/modules/shared/components/assistant/HalEye";
import { Button } from "@/modules/shared/components/ui/button";
import { Textarea } from "@/modules/shared/components/ui/textarea";
import { ScrollArea } from "@/modules/shared/components/ui/scroll-area";
import { cn } from "@/modules/shared/lib/utils";
import ReactMarkdown from "react-markdown";
import { log, reportError } from "@/modules/shared/lib/observability";
import { getSessionToken } from "@/modules/shared/lib/get-current-user";
import { useAssistantSettings } from "@/modules/shared/hooks/useAssistantSettings";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const CHAT_URL = `${import.meta.env.VITE_API_URL}/assistant-ai`;

export function AssistantChat() {
  const { settings } = useAssistantSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const color = settings.primary_color || "#10b981";
  const logoUrl = settings.logo_url;

  // Renders the full avatar circle (container + icon/image)
  const AssistantAvatar = ({ size = "md" }: { size?: "sm" | "md" | "lg" | "fab" }) => {
    const sizeMap = {
      sm: { container: "h-7 w-7", img: "h-7 w-7", icon: "h-3.5 w-3.5" },
      md: { container: "h-11 w-11", img: "h-11 w-11", icon: "h-5 w-5" },
      lg: { container: "h-14 w-14", img: "h-14 w-14", icon: "h-7 w-7" },
      fab: { container: "h-12 w-12", img: "h-12 w-12", icon: "h-5 w-5" },
    };
    const s = sizeMap[size];

    if (logoUrl) {
      return (
        <img
          src={logoUrl}
          alt={settings.assistant_name}
          className={cn(s.img, "rounded-full object-cover")}
        />
      );
    }

    return <HalEye size={size} color={color} />;
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  const getAuthToken = async (): Promise<string> => {
    const sessionToken = await getSessionToken();
    if (!sessionToken) {
      throw new Error("Sessão expirada. Faça login novamente.");
    }
    return sessionToken;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 50 * 1024;
    const allowedTypes = [".pdf", ".txt", ".md", ".docx"];
    const ext = "." + file.name.split(".").pop()?.toLowerCase();

    if (!allowedTypes.includes(ext)) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "⚠️ Formato não suportado. Envie arquivos .pdf, .txt, .md ou .docx."
      }]);
      return;
    }

    try {
      const text = await file.text();
      const truncated = text.slice(0, maxSize);
      const prefix = `[DOCUMENTO ANEXADO: ${file.name}]\n\n`;
      streamChat(prefix + truncated);
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "⚠️ Erro ao ler o arquivo. Tente colar o conteúdo diretamente no chat."
      }]);
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const streamChat = async (userMessage: string) => {
    const userMsg: Message = { role: "user", content: userMessage };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setIsLoading(true);
    setInput("");

    let assistantContent = "";

    try {
      const token = await getAuthToken();

      const response = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ messages: newMessages }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch((err) => { reportError(err, "assistant-response-parse"); return {}; });
        throw new Error(errorData.error || "Erro ao enviar mensagem");
      }

      if (!response.body) {
        throw new Error("Stream não disponível");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant") {
                  return prev.map((m, i) =>
                    i === prev.length - 1 ? { ...m, content: assistantContent } : m
                  );
                }
                return [...prev, { role: "assistant", content: assistantContent }];
              });
            }
          } catch {
            buffer = line + "\n" + buffer;
            break;
          }
        }
      }
    } catch (error) {
      log.error("Assistant AI error", {}, error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: error instanceof Error ? error.message : (settings.error_message || "Desculpe, ocorreu um erro. Tente novamente."),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;
    streamChat(input.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSuggestion = (suggestion: string) => {
    streamChat(suggestion);
  };

  // Don't render if disabled
  if (!settings.is_enabled) return null;

  const suggestions = settings.suggestions || [];
  const assistantName = settings.assistant_name || "Assistente IA";
  const assistantSubtitle = settings.assistant_subtitle || "";

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.txt,.md,.docx"
        className="hidden"
        onChange={handleFileUpload}
      />

      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50"
          >
            <Button
              onClick={() => setIsOpen(true)}
              className={cn(
                "h-12 w-12 rounded-full shadow-lg border-0 transition-all duration-300 hover:scale-110 p-0 overflow-hidden",
                logoUrl ? "bg-transparent" : "bg-[#050510] hover:bg-[#0d0d1a]"
              )}
              style={{ boxShadow: `0 8px 24px ${color}40` }}
            >
              <AssistantAvatar size="fab" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 w-[380px] max-w-[calc(100vw-32px)] md:max-w-[calc(100vw-48px)] h-[500px] max-h-[calc(100vh-80px)] md:max-h-[calc(100vh-100px)] bg-background rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            style={{ borderColor: `${color}33`, borderWidth: 1, boxShadow: `0 25px 50px ${color}15` }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between p-4 border-b"
              style={{ borderColor: `${color}33`, background: `linear-gradient(135deg, ${color}18, ${color}08, transparent)` }}
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="shadow-lg rounded-full" style={logoUrl ? {} : { boxShadow: `0 4px 12px ${color}40` }}>
                    <AssistantAvatar size="md" />
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-background" style={{ background: `${color}cc` }} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{assistantName}</h3>
                  {assistantSubtitle && (
                    <p className="text-xs font-medium" style={{ color }}>{assistantSubtitle}</p>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground"
                style={{ ["--hover-bg" as any]: `${color}15` }}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center space-y-6 py-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", bounce: 0.5 }}
                    className="relative"
                  >
                    <div className="h-20 w-20 rounded-full flex items-center justify-center" style={{ background: `${color}20` }}>
                      <motion.div
                        className="rounded-full overflow-hidden shadow-lg"
                        style={logoUrl ? {} : { boxShadow: `0 4px 12px ${color}40` }}
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <AssistantAvatar size="lg" />
                      </motion.div>
                    </div>
                    <motion.div
                      className="absolute -top-1 -right-1"
                      animate={{ rotate: [0, 15, -15, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Sparkles className="h-5 w-5" style={{ color }} />
                    </motion.div>
                  </motion.div>
                  <div>
                    <h4 className="font-semibold text-lg mb-1">
                      {(settings.greeting_message || "Olá! Sou o {name}").replace("{name}", assistantName)}
                    </h4>
                    <p className="text-sm text-muted-foreground max-w-[280px]">
                      {settings.greeting_description || "Posso consultar dados, registrar sessões 1:1, avaliações e muito mais. Envie um arquivo ou pergunte!"}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center px-2">
                    {suggestions.map((suggestion, index) => (
                      <motion.div
                        key={suggestion}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs transition-all"
                          style={{ borderColor: `${color}50` }}
                          onClick={() => handleSuggestion(suggestion)}
                        >
                          {suggestion}
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={cn(
                        "flex",
                        message.role === "user" ? "justify-end" : "justify-start"
                      )}
                    >
                      {message.role === "assistant" && (
                        <div className="mr-2 mt-0.5 shrink-0 shadow-sm rounded-full overflow-hidden">
                          <AssistantAvatar size="sm" />
                        </div>
                      )}
                      <div
                        className={cn(
                          "max-w-[80%] rounded-2xl px-4 py-2.5 text-sm",
                          message.role === "user"
                            ? "text-white rounded-br-md shadow-md"
                            : "bg-muted/80 rounded-bl-md border border-border/50"
                        )}
                        style={message.role === "user" ? { background: `linear-gradient(135deg, ${color}, ${color}dd)` } : undefined}
                      >
                        {message.role === "assistant" ? (
                          <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:my-2 [&>ol]:my-2 [&>ul]:pl-4 [&>ol]:pl-4">
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </div>
                        ) : (
                          <span className="whitespace-pre-wrap">{message.content.startsWith("[DOCUMENTO ANEXADO") ? `${message.content.split("\n")[0]}` : message.content}</span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                  {isLoading && messages[messages.length - 1]?.role === "user" && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="mr-2 shrink-0 rounded-full overflow-hidden">
                        <AssistantAvatar size="sm" />
                      </div>
                      <div className="bg-muted/80 rounded-2xl rounded-bl-md px-4 py-3 border border-border/50">
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            <motion.span className="h-2 w-2 rounded-full" style={{ background: color }} animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0 }} />
                            <motion.span className="h-2 w-2 rounded-full" style={{ background: color }} animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }} />
                            <motion.span className="h-2 w-2 rounded-full" style={{ background: color }} animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }} />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <form
              onSubmit={handleSubmit}
              className="p-4 border-t"
              style={{ borderColor: `${color}33`, background: `linear-gradient(180deg, ${color}08, transparent)` }}
            >
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-[44px] w-[44px] text-muted-foreground"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading}
                  title="Enviar arquivo"
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
                <Textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={settings.input_placeholder || "Digite ou cole uma ata..."}
                  className="min-h-[44px] max-h-[120px] resize-none bg-background"
                  style={{ borderColor: `${color}33` }}
                  rows={1}
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!input.trim() || isLoading}
                  className="shrink-0 h-[44px] w-[44px] shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                  style={{ background: `linear-gradient(135deg, ${color}, ${color}dd)` }}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
